import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
  AdminUserApiModel,
  AdminUserDataService,
  CountryDataService,
  CountryGroupDetailApiModel,
  FeedDataService,
  GenreDataService,
  PlatformApiModel,
  PlatformDataService,
  UserSegmentApiModel,
  UserSegmentDataService,
  AdminUiDetailsDataService,
  AvailablePageItemsDataService,
} from '@cxb/editor-stb-api';
import { VersionApiModel, StreamingVersionDataService } from '@cxb/content-stb-api';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { RouterState } from '@ngrx/router-store';
import { Store } from '@ngrx/store';
import { of, filter } from 'rxjs';
import { catchError, map, switchMap, withLatestFrom } from 'rxjs/operators';
import * as AppActions from './app.actions';
import { RootAppState } from './app.reducer';
import { selectCxbRootLevelRoles, selectCxbSelectedTenant } from './app.selectors';
import { AuthService } from './core/services/auth.service';
import { PageAccessesService } from './core/services/page-access/page-access.service';
import { Country } from './domain/country';
import { Platform } from './domain/platform';
import { apiErrorMessage } from './shared/functions/general-api-error-handling';
import { mapCountryApiModelsToCountryDomains } from './shared/functions/map-country-api-to-domain';
import { mapUserSegmentsApiToDomain } from './shared/functions/map-user-segment-api-to-domain';
import { mapFeed } from './shared/functions/feed-mapper';
import { mapApiAdminUIDetailsToDomain } from './admin-ui-details/mapping/map-admin-ui-details-api-to-domain';
import { theme as themeDefault } from '../../../osn-shared/styles/variables/colors-default';
import { theme as themePurple } from '../../../osn-shared/styles/variables/colors-purple';
import { takeFirst } from 'osn-shared';

@Injectable()
export class AppEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly userSegmentDataService: UserSegmentDataService,
    private readonly streamingVersionDataService: StreamingVersionDataService,
    private readonly countryDataService: CountryDataService,
    private readonly platformDataService: PlatformDataService,
    private readonly adminUserDataService: AdminUserDataService,
    private readonly authService: AuthService,
    private readonly store: Store<RootAppState & RouterState>,
    private readonly pageAccessService: PageAccessesService,
    private readonly router: Router,
    private readonly genreDataService: GenreDataService,
    private readonly feedDataService: FeedDataService,
    private readonly adminUiDetailsDataService: AdminUiDetailsDataService,
    private readonly availablePageItemsDataService: AvailablePageItemsDataService
  ) {}

  private readonly userId$ = this.authService.user$.pipe(
    filter((user) => !!user),
    map((user) => user?.localAccountId)
  );

  public loadUserSegments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.loadCxbUserSegments),
      switchMap(() =>
        this.userSegmentDataService.getUserSegments().pipe(
          map((apiModel: UserSegmentApiModel[]) =>
            AppActions.loadCxbUserSegmentsSuccess({ userSegments: mapUserSegmentsApiToDomain(apiModel) })
          ),
          catchError((error) => of(AppActions.loadCxbUserSegmentsFail(apiErrorMessage(error))))
        )
      )
    )
  );

  public loadPlatforms$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.loadCxbPlatforms, AppActions.saveCxbUserSelectedTenantSuccess, AppActions.loadCxbUserSelectedTenantSuccess),
      withLatestFrom(this.store.select(selectCxbSelectedTenant)),
      filter(([, selectedTenant]) => !!selectedTenant?.id),
      switchMap(([, selectedTenant]) => {
        return this.platformDataService.getPlatforms(selectedTenant?.id).pipe(
          map((apiModel: PlatformApiModel[]) => AppActions.loadCxbPlatformsSuccess({ platforms: this.mapPlatforms(apiModel) })),
          catchError((error) => of(AppActions.loadCxbPlatformsFail(apiErrorMessage(error))))
        );
      })
    )
  );

  public fetchGenres$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.fetchGenres),
      switchMap(() =>
        this.genreDataService.getGenres().pipe(
          map((result) =>
            AppActions.fetchGenresSuccess({
              genres:
                result && result.length
                  ? result.map((genre) => ({
                      id: genre.id,
                      title: genre.title,
                      guid: genre.guid,
                    }))
                  : [],
            })
          ),
          catchError((error) => of(AppActions.fetchGenresFail(apiErrorMessage(error))))
        )
      )
    )
  );

  public fetchFeeds$ = AppActions.fetchCxbFeeds.createEffect$(this.actions$, () =>
    this.store.select(selectCxbSelectedTenant).pipe(
      filter((selectedTenant) => !!selectedTenant?.id),
      takeFirst(),
      switchMap((selectedTenant) => {
        if (selectedTenant) {
          return this.feedDataService.getFeedsByCurrentUser(null, selectedTenant?.id).pipe(
            map((feeds) => ({
              feeds: feeds.map(mapFeed),
            }))
          );
        }
        return of(null);
      })
    )
  );

  public fetchFeedsOnTennantChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.saveCxbUserSelectedTenantSuccess),
      switchMap(() => {
        return of(AppActions.fetchCxbFeeds.sourceAction());
      })
    )
  );

  public loadCountries$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.loadCxbCountries),
      switchMap(() =>
        this.countryDataService.getCountryGroups().pipe(
          map((countryGroups: CountryGroupDetailApiModel[]) =>
            AppActions.loadCxbCountriesSuccess({
              countries: this.mapCountyGroupApiModelToCountries(countryGroups),
              countryGroups: countryGroups.map((cg) => {
                return {
                  id: cg.id,
                  name: cg.countryGroupName,
                  countries: cg.countries.map((c) => {
                    return {
                      id: c.id,
                      name: c.countryName,
                      iso2Code: c.iso2Code,
                    };
                  }),
                };
              }),
            })
          ),
          catchError((error) => of(AppActions.loadCxbCountriesFail(apiErrorMessage(error))))
        )
      )
    )
  );

  private mapCountyGroupApiModelToCountries(countryGroups: CountryGroupDetailApiModel[]): Country[] {
    return countryGroups
      .reduce((countries: Country[], countryGroup: CountryGroupDetailApiModel) => {
        const newCountries = countryGroup.countries.filter(({ id }) => !countries.some((country) => country.id === id));
        return [...countries, ...mapCountryApiModelsToCountryDomains(newCountries)];
      }, [])
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  private mapPlatforms(apiModels: PlatformApiModel[]): Platform[] {
    return apiModels.map((apiModel: PlatformApiModel) => ({
      id: apiModel.id,
      name: apiModel.name,
    }));
  }

  public loadUserData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.loadCxbUserData),
      switchMap(() => this.userId$),
      switchMap((userId) =>
        this.adminUserDataService.getMe().pipe(
          map(({ roles, tenants }: AdminUserApiModel) => {
            return AppActions.loadCxbUserDataSuccess({
              userId,
              rootLevelRoles: roles,
              availableTenants: tenants,
            });
          }),
          catchError((error) => of(AppActions.loadCxbUserDataFail(apiErrorMessage(error))))
        )
      )
    )
  );

  public saveUserSelectedTenant$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.saveCxbUserSelectedTenant),
      switchMap(({ tenant, userId }) => {
        localStorage.setItem(`SELECTED_TENANT_FOR_USER_${userId}`, tenant?.id);
        return of(AppActions.saveCxbUserSelectedTenantSuccess({ tenant }));
      }),
      catchError((error) => of(AppActions.saveCxbUserSelectedTenantFail(error)))
    )
  );

  public loadUserSelectedTenant$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.loadCxbUserDataSuccess),
      switchMap(({ userId, rootLevelRoles, availableTenants }) => {
        if (availableTenants && availableTenants.length === 1) {
          // The only available tenant should be selected explicitly
          return of(AppActions.saveCxbUserSelectedTenant({ tenant: availableTenants[0], userId }));
        }

        const savedTenantId = localStorage.getItem(`SELECTED_TENANT_FOR_USER_${userId}`);
        if (!savedTenantId) {
          // There are no saved tenant data
          return of(AppActions.loadCxbUserSelectedTenantSuccess({ tenant: null }));
        }

        if (!availableTenants || !availableTenants.length) {
          // There are no tenant options, no matter what the user saved previously, it is now invalid
          return of(AppActions.loadCxbUserSelectedTenantSuccess({ tenant: null }));
        }

        // Find the saved tenant in the available tenant options
        const existingTenantOption = availableTenants.find((to) => to.id === savedTenantId);

        return of(AppActions.loadCxbUserSelectedTenantSuccess({ tenant: existingTenantOption || null }));
      }),
      catchError((error) => of(AppActions.loadCxbUserSelectedTenantFail(error)))
    )
  );

  public setRolesOfSelectedTenant$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AppActions.saveCxbUserSelectedTenant, AppActions.loadCxbUserSelectedTenantSuccess),
        switchMap(({ tenant }) => {
          if (!this.router.url.startsWith('/root')) {
            if (tenant) {
              this.pageAccessService.setUsersPageAccesses(tenant.roles);
            } else {
              console.error('No tenant was selected, could not set tenant roles!');
              this.pageAccessService.setUsersPageAccesses([]);
            }
          }
          return of(null);
        })
      ),
    { dispatch: false }
  );

  public setRolesOfSelectedTenantWhenLeavingTenantIndependentRoute$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AppActions.leaveTenantIndependentRoute),
        switchMap(({ tenant }) => {
          if (tenant) {
            this.pageAccessService.setUsersPageAccesses(tenant.roles);
          } else {
            console.error('No tenant was selected or the active page is tenant independent, could not set tenant roles!');
          }
          return of(null);
        })
      ),
    { dispatch: false }
  );

  public setRootLevelRoles$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AppActions.enterTenantIndependentRoute),
        withLatestFrom(this.store.select(selectCxbRootLevelRoles)),
        switchMap(([, roles]) => {
          this.pageAccessService.setUsersPageAccesses(roles);
          return of(null);
        })
      ),
    { dispatch: false }
  );

  public switchTenant$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AppActions.switchCxbTenant),
        switchMap(({ userId }) => {
          localStorage.removeItem(`SELECTED_TENANT_FOR_USER_${userId}`);
          return of(null);
        })
      ),
    { dispatch: false }
  );

  public fetchAdminUIDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.saveCxbUserSelectedTenantSuccess, AppActions.loadCxbUserSelectedTenantSuccess),
      switchMap(() => {
        return of(AppActions.fetchAdminUIDetails());
      })
    )
  );

  public saveAdminUIDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.fetchAdminUIDetails),
      withLatestFrom(this.store.select(selectCxbSelectedTenant)),
      switchMap(([, selectedTenant]) => {
        if (selectedTenant) {
          const root = document.documentElement;
          Object.entries(selectedTenant?.id === 'dc2b24d4-9a59-42c4-b553-e337560029b0' ? themePurple : themeDefault).forEach((item) => {
            const [key, value] = item;
            root.style.setProperty(`--${key}`, value);
          });

          return this.adminUiDetailsDataService.getAdminUiDetails(selectedTenant?.id).pipe(
            switchMap((adminUIDetailApiModel) => of(mapApiAdminUIDetailsToDomain(adminUIDetailApiModel))),
            map((adminUIDetails) => AppActions.fetchAdminUIDetailsSuccess({ adminUIDetails }))
          );
        } else {
          const root = document.documentElement;
          Object.entries(themeDefault).forEach(([key, value]) => {
            root.style.setProperty(`--${key}`, value);
          });
        }
        return [];
      })
    )
  );

  public fetchApiVersion$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.fetchApiVersion),
      switchMap(() =>
        this.streamingVersionDataService.version().pipe(
          map((result: VersionApiModel) => {
            return AppActions.fetchApiVersionSuccess({
              version: result.version,
            });
          }),
          catchError((error) => of(AppActions.fetchApiVersionFail(apiErrorMessage(error))))
        )
      )
    )
  );

  public fetchTrayOptions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.saveCxbUserSelectedTenantSuccess, AppActions.loadCxbUserSelectedTenantSuccess),
      switchMap(() => {
        return of(AppActions.fetchTrayOptions());
      })
    )
  );

  public saveTrayOptions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.fetchTrayOptions),
      withLatestFrom(this.store.select(selectCxbSelectedTenant)),
      switchMap(([, selectedTenant]) => {
        if (selectedTenant) {
          return this.availablePageItemsDataService.getAvailablePageItems(selectedTenant?.id).pipe(
            // switchMap((trayOptions) => of(mapApiAdminUIDetailsToDomain(adminUIDetailApiModel))), //TODO1203:map??
            map((trayOptions) => AppActions.fetchTrayOptionsSuccess({ trayOptions })),
            catchError((error) => of(AppActions.fetchTrayOptionsFail(apiErrorMessage(error))))
          );
        }
        return [];
      })
    )
  );
}
